/*
 * SOLO SUPPLY - PREMIUM ANIMATIONS
 * Ultra-Premium Interactive Effects
 * File: assets/solo-supply-animations.js
 */

class SoloSupplyPremium {
    constructor() {
        this.init();
    }

    init() {
        this.initPageLoader();
        this.initSmoothScroll();
        this.initScrollAnimations();
        this.initHeaderScroll();
        this.initMobileMenu();
        // this.initCartDrawer(); // Handled by solo-supply.js
        this.initProductQuickAdd();
        this.initCountdown();
        this.initMarquee();
        this.initParallax();
        this.initButtonEffects();
    }

    // ========================================
    // PAGE LOADER
    // ========================================
    initPageLoader() {
        window.addEventListener('load', () => {
            const loader = document.querySelector('.page-loader');
            if (loader) {
                setTimeout(() => {
                    loader.classList.add('hidden');
                    setTimeout(() => loader.remove(), 500);
                }, 1000);
            }
        });
    }


    // ========================================
    // SMOOTH SCROLL
    // ========================================
    initSmoothScroll() {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
                const href = this.getAttribute('href');
                if (href !== '#' && href !== '') {
                    e.preventDefault();
                    const target = document.querySelector(href);
                    if (target) {
                        target.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                }
            });
        });
    }

    // ========================================
    // SCROLL ANIMATIONS (INTERSECTION OBSERVER)
    // ========================================
    initScrollAnimations() {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -100px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry, index) => {
                if (entry.isIntersecting) {
                    // Stagger animation
                    setTimeout(() => {
                        entry.target.classList.add('animated');
                    }, index * 100);
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        // Observe all animated elements
        document.querySelectorAll('.animate-on-scroll, .animate-slide-left, .animate-scale').forEach(el => {
            observer.observe(el);
        });

        // Observe product cards for stagger effect
        document.querySelectorAll('.product-card-premium').forEach((card, index) => {
            card.style.opacity = '0';
            card.style.transform = 'translateY(30px)';

            setTimeout(() => {
                card.style.transition = 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
                card.style.opacity = '1';
                card.style.transform = 'translateY(0)';
            }, index * 100);
        });
    }

    // ========================================
    // HEADER SCROLL EFFECT
    // ========================================
    initHeaderScroll() {
        const header = document.querySelector('.header-premium');
        if (!header) return;

        let lastScroll = 0;

        window.addEventListener('scroll', () => {
            const currentScroll = window.pageYOffset;

            if (currentScroll > 10) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }

            lastScroll = currentScroll;
        });
    }

    // ========================================
    // MOBILE MENU
    // ========================================
    initMobileMenu() {
        const toggle = document.querySelector('.header-premium__mobile-toggle');
        const menu = document.querySelector('.mobile-menu-premium');
        const overlay = document.querySelector('.mobile-menu-premium__overlay');

        if (!toggle || !menu) return;

        const openMenu = () => {
            toggle.classList.add('active');
            menu.classList.add('open');
            overlay?.classList.add('visible');
            document.body.style.overflow = 'hidden';

            // Stagger menu items
            const items = menu.querySelectorAll('.mobile-menu-premium__link');
            items.forEach((item, index) => {
                item.style.opacity = '0';
                item.style.transform = 'translateX(-30px)';
                setTimeout(() => {
                    item.style.transition = 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)';
                    item.style.opacity = '1';
                    item.style.transform = 'translateX(0)';
                }, index * 80);
            });
        };

        const closeMenu = () => {
            toggle.classList.remove('active');
            menu.classList.remove('open');
            overlay?.classList.remove('visible');
            document.body.style.overflow = '';
        };

        toggle.addEventListener('click', () => {
            if (toggle.classList.contains('active')) {
                closeMenu();
            } else {
                openMenu();
            }
        });

        overlay?.addEventListener('click', closeMenu);
    }

    // ========================================
    // CART DRAWER
    // ========================================
    initCartDrawer() {
        const cartIcon = document.querySelector('[data-cart-icon]');
        const drawer = document.querySelector('.cart-drawer-premium');
        const overlay = document.querySelector('.cart-drawer-premium__overlay');
        const closeBtn = document.querySelector('.cart-drawer-premium__close');

        if (!drawer) return;

        const openCart = () => {
            drawer.classList.add('open');
            overlay?.classList.add('visible');
            document.body.style.overflow = 'hidden';
            this.updateCart();
        };

        const closeCart = () => {
            drawer.classList.remove('open');
            overlay?.classList.remove('visible');
            document.body.style.overflow = '';
        };

        cartIcon?.addEventListener('click', openCart);
        closeBtn?.addEventListener('click', closeCart);
        overlay?.addEventListener('click', closeCart);

        // Listen for cart updates
        document.addEventListener('cart:updated', openCart);
    }

    // ========================================
    // UPDATE CART
    // ========================================
    async updateCart() {
        try {
            const response = await fetch('/cart.js');
            const cart = await response.json();

            // Update cart count
            const countEl = document.querySelector('.header-premium__cart-count');
            if (countEl) {
                countEl.textContent = cart.item_count;
            }

            // Update cart items
            const itemsContainer = document.querySelector('.cart-drawer-premium__items');
            const subtotalEl = document.querySelector('.cart-drawer-premium__subtotal-amount');

            if (itemsContainer) {
                if (cart.items.length === 0) {
                    itemsContainer.innerHTML = '<p style="text-align: center; padding: 2rem; opacity: 0.6;">Your cart is empty</p>';
                } else {
                    itemsContainer.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
                    this.initCartItemListeners();
                }
            }

            if (subtotalEl) {
                subtotalEl.textContent = this.formatMoney(cart.total_price);
            }
        } catch (error) {
            console.error('Cart update error:', error);
        }
    }

    renderCartItem(item) {
        return `
      <div class="cart-item-premium" data-key="${item.key}">
        <img src="${item.image}" alt="${item.title}" class="cart-item-premium__image">
        <div class="cart-item-premium__details">
          <div class="cart-item-premium__title">${item.product_title}</div>
          ${item.variant_title ? `<div class="cart-item-premium__variant">${item.variant_title}</div>` : ''}
          <div class="cart-item-premium__price">${this.formatMoney(item.final_line_price)}</div>
          <div class="cart-item-premium__quantity">
            <button class="qty-btn" data-qty-decrease="${item.key}">−</button>
            <span class="qty-value">${item.quantity}</span>
            <button class="qty-btn" data-qty-increase="${item.key}">+</button>
          </div>
        </div>
        <button class="cart-item-premium__remove" data-remove="${item.key}">×</button>
      </div>
    `;
    }

    initCartItemListeners() {
        // Increase quantity
        document.querySelectorAll('[data-qty-increase]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const key = e.target.dataset.qtyIncrease;
                const qtyEl = e.target.previousElementSibling;
                const newQty = parseInt(qtyEl.textContent) + 1;
                await this.updateCartQuantity(key, newQty);
            });
        });

        // Decrease quantity
        document.querySelectorAll('[data-qty-decrease]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const key = e.target.dataset.qtyDecrease;
                const qtyEl = e.target.nextElementSibling;
                const newQty = Math.max(1, parseInt(qtyEl.textContent) - 1);
                await this.updateCartQuantity(key, newQty);
            });
        });

        // Remove item
        document.querySelectorAll('[data-remove]').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const key = e.target.dataset.remove;
                await this.updateCartQuantity(key, 0);
            });
        });
    }

    async updateCartQuantity(key, quantity) {
        try {
            await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity })
            });
            await this.updateCart();
            this.showToast(quantity === 0 ? 'Item removed' : 'Cart updated');
        } catch (error) {
            console.error('Update error:', error);
        }
    }

    // ========================================
    // PRODUCT QUICK ADD
    // ========================================
    initProductQuickAdd() {
        document.addEventListener('click', async (e) => {
            const btn = e.target.closest('[data-quick-add]');
            if (!btn) return;

            e.preventDefault();
            const variantId = btn.dataset.variantId;
            if (!variantId) return;

            btn.disabled = true;
            btn.textContent = 'Adding...';

            try {
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: variantId, quantity: 1 })
                });

                document.dispatchEvent(new CustomEvent('cart:updated'));
                this.showToast('Added to cart!');
                btn.textContent = 'Quick Add';
            } catch (error) {
                console.error('Add to cart error:', error);
                btn.textContent = 'Error';
            } finally {
                btn.disabled = false;
            }
        });
    }

    // ========================================
    // COUNTDOWN TIMER
    // ========================================
    initCountdown() {
        const countdowns = document.querySelectorAll('[data-countdown-date]');

        countdowns.forEach(countdown => {
            const targetDate = new Date(countdown.dataset.countdownDate).getTime();

            const updateCountdown = () => {
                const now = new Date().getTime();
                const distance = targetDate - now;

                if (distance < 0) {
                    countdown.innerHTML = '<div class="countdown-expired">DROP IS LIVE!</div>';
                    return;
                }

                const days = Math.floor(distance / (1000 * 60 * 60 * 24));
                const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                const daysEl = countdown.querySelector('[data-days]');
                const hoursEl = countdown.querySelector('[data-hours]');
                const minutesEl = countdown.querySelector('[data-minutes]');
                const secondsEl = countdown.querySelector('[data-seconds]');

                if (daysEl) this.flipNumber(daysEl, days);
                if (hoursEl) this.flipNumber(hoursEl, hours);
                if (minutesEl) this.flipNumber(minutesEl, minutes);
                if (secondsEl) this.flipNumber(secondsEl, seconds);
            };

            updateCountdown();
            setInterval(updateCountdown, 1000);
        });
    }

    flipNumber(element, newValue) {
        const currentValue = element.textContent;
        const newValueStr = String(newValue).padStart(2, '0');

        if (currentValue !== newValueStr) {
            element.style.transform = 'rotateX(90deg)';
            setTimeout(() => {
                element.textContent = newValueStr;
                element.style.transform = 'rotateX(0deg)';
            }, 150);
        }
    }

    // ========================================
    // BRAND MARQUEE
    // ========================================
    initMarquee() {
        const marquees = document.querySelectorAll('.brand-marquee__track');

        marquees.forEach(track => {
            // Clone items for infinite scroll
            const items = track.innerHTML;
            track.innerHTML = items + items;
        });
    }

    // ========================================
    // PARALLAX EFFECT
    // ========================================
    initParallax() {
        const parallaxElements = document.querySelectorAll('[data-parallax]');

        window.addEventListener('scroll', () => {
            const scrolled = window.pageYOffset;

            parallaxElements.forEach(el => {
                const speed = el.dataset.parallax || 0.5;
                const yPos = -(scrolled * speed);
                el.style.transform = `translateY(${yPos}px)`;
            });
        });
    }

    // ========================================
    // BUTTON EFFECTS
    // ========================================
    initButtonEffects() {
        document.querySelectorAll('.btn-premium').forEach(btn => {
            btn.addEventListener('click', function (e) {
                // Scale down effect
                this.style.transform = 'scale(0.95)';
                setTimeout(() => {
                    this.style.transform = '';
                }, 150);
            });
        });
    }

    // ========================================
    // TOAST NOTIFICATIONS
    // ========================================
    showToast(message, type = 'success') {
        let container = document.querySelector('.toast-container-premium');

        if (!container) {
            container = document.createElement('div');
            container.className = 'toast-container-premium';
            document.body.appendChild(container);
        }

        const toast = document.createElement('div');
        toast.className = `toast-premium toast-premium--${type}`;
        toast.textContent = message;

        container.appendChild(toast);

        setTimeout(() => toast.classList.add('show'), 10);

        setTimeout(() => {
            toast.classList.remove('show');
            setTimeout(() => toast.remove(), 300);
        }, 3000);
    }

    // ========================================
    // UTILITY
    // ========================================
    formatMoney(cents) {
        return `$${(cents / 100).toFixed(2)}`;
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.soloSupplyPremium = new SoloSupplyPremium();
    });
} else {
    window.soloSupplyPremium = new SoloSupplyPremium();
}

// Add dynamic styles for toast and cart items
const dynamicStyles = document.createElement('style');
dynamicStyles.textContent = `
  .toast-container-premium {
    position: fixed;
    top: 100px;
    right: 20px;
    z-index: 10000;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .toast-premium {
    background: var(--color-dark-card);
    color: var(--color-white);
    padding: 1rem 1.5rem;
    border-radius: var(--radius-md);
    border-left: 4px solid var(--color-neon-green);
    box-shadow: var(--glow-neon-sm), var(--shadow-lg);
    transform: translateX(400px);
    opacity: 0;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    min-width: 250px;
    font-weight: 600;
  }

  .toast-premium.show {
    transform: translateX(0);
    opacity: 1;
  }

  .cart-item-premium {
    display: flex;
    gap: 1rem;
    padding: 1rem;
    background: rgba(255, 255, 255, 0.02);
    border-radius: var(--radius-md);
    margin-bottom: 1rem;
    border: 1px solid rgba(57, 255, 20, 0.1);
    transition: all 0.3s;
    position: relative;
  }

  .cart-item-premium:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(57, 255, 20, 0.3);
  }

  .cart-item-premium__image {
    width: 80px;
    height: 100px;
    object-fit: cover;
    border-radius: var(--radius-sm);
  }

  .cart-item-premium__details {
    flex: 1;
  }

  .cart-item-premium__title {
    font-weight: 600;
    margin-bottom: 0.25rem;
    font-size: 0.95rem;
  }

  .cart-item-premium__variant {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    margin-bottom: 0.5rem;
  }

  .cart-item-premium__price {
    color: var(--color-neon-green);
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }

  .cart-item-premium__quantity {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .qty-btn {
    width: 30px;
    height: 30px;
    background: rgba(57, 255, 20, 0.1);
    border: 1px solid var(--color-neon-green);
    border-radius: var(--radius-sm);
    color: var(--color-neon-green);
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .qty-btn:hover {
    background: var(--color-neon-green);
    color: var(--color-black);
    transform: scale(1.1);
  }

  .qty-value {
    font-weight: 700;
    min-width: 30px;
    text-align: center;
  }

  .cart-item-premium__remove {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    width: 24px;
    height: 24px;
    background: rgba(255, 0, 0, 0.2);
    border: 1px solid #ff0000;
    border-radius: 50%;
    color: #ff0000;
    font-size: 1.2rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }

  .cart-item-premium__remove:hover {
    background: #ff0000;
    color: white;
    transform: rotate(90deg);
  }

  .mobile-menu-premium {
    position: fixed;
    top: 0;
    left: -100%;
    width: 100%;
    max-width: 400px;
    height: 100vh;
    background: var(--color-dark-card);
    z-index: 2500;
    padding: var(--spacing-2xl);
    transition: left 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    overflow-y: auto;
    border-right: 2px solid var(--color-neon-green);
  }

  .mobile-menu-premium.open {
    left: 0;
  }

  .mobile-menu-premium__overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--color-dark-overlay);
    z-index: 2499;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s;
    backdrop-filter: blur(5px);
  }

  .mobile-menu-premium__overlay.visible {
    opacity: 1;
    visibility: visible;
  }

  .mobile-menu-premium__link {
    display: block;
    font-family: var(--font-heading);
    font-size: 2rem;
    text-transform: uppercase;
    color: var(--color-white);
    padding: 1rem 0;
    border-bottom: 1px solid rgba(57, 255, 20, 0.1);
    transition: all 0.3s;
  }

  .mobile-menu-premium__link:hover {
    color: var(--color-neon-green);
    padding-left: 1rem;
  }
`;
document.head.appendChild(dynamicStyles);
